/**
 * Calculate dynamic package labels based on PAX number and categories data
 * @param {Array} categoriesData - Array of category objects with choose_rule
 * @param {number} paxNumber - Selected PAX number from filters
 * @returns {string} - Formatted string like "2x Salads, 1x Appetizers"
 */
export const calculatePackageLabels = (categoriesData, paxNumber) => {
  if (!categoriesData || !Array.isArray(categoriesData) || !paxNumber) {
    return '';
  }

  const labels = [];

  categoriesData.forEach(category => {
    if (!category.choose_rule || !Array.isArray(category.choose_rule)) {
      return;
    }

    // Find the appropriate choose rule for the given pax number
    // Sort rules by pax descending and find the first one where paxNumber >= rule.pax
    const sortedRules = [...category.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    const matchingRule = sortedRules.find(rule => 
      paxNumber >= parseInt(rule.pax)
    );

    if (matchingRule && matchingRule.choose > 0) {
      const categoryName = category.name_en || category.name_ar || 'Items';
      labels.push(`${matchingRule.choose}x ${categoryName}`);
    }
  });

  return labels.join(', ');
};

/**
 * Get individual category limits for a specific PAX number
 * @param {Array} categoriesData - Array of category objects with choose_rule
 * @param {number} paxNumber - Selected PAX number from filters
 * @returns {Array} - Array of objects with category info and limits
 */
export const getCategoryLimitsForPax = (categoriesData, paxNumber) => {
  if (!categoriesData || !Array.isArray(categoriesData) || !paxNumber) {
    return [];
  }

  const categoryLimits = [];

  categoriesData.forEach(category => {
    if (!category.choose_rule || !Array.isArray(category.choose_rule)) {
      return;
    }

    // Find the appropriate choose rule for the given pax number
    // Sort rules by pax descending and find the first one where paxNumber >= rule.pax
    const sortedRules = [...category.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    const matchingRule = sortedRules.find(rule => 
      paxNumber >= parseInt(rule.pax)
    );

    if (matchingRule && matchingRule.choose > 0) {
      categoryLimits.push({
        categoryId: category.id,
        categoryName: category.name_en || category.name_ar || 'Items',
        itemLimit: matchingRule.choose,
        paxRangeFrom: parseInt(matchingRule.pax),
        paxRangeTo: parseInt(matchingRule.pax) // In new structure, each rule is for a specific pax
      });
    }
  });

  return categoryLimits;
};

/**
 * Check if PAX can be decreased without violating category limits
 * Validates that items in cart won't exceed new category limits
 * @param {Object} packageData - Package object containing categories and items
 * @param {number} currentPax - Current PAX number
 * @param {number} newPax - Target PAX number (lower than current)
 * @returns {Object} - Validation result with canDecrease flag and categories info
 */
export const canDecreasePax = (packageData, currentPax, newPax) => {
  if (!packageData || !currentPax || !newPax || newPax >= currentPax) {
    return { canDecrease: true, categoriesExceeded: [] };
  }

  const categoriesData = packageData.categories;
  if (!categoriesData || !Array.isArray(categoriesData)) {
    return { canDecrease: true, categoriesExceeded: [] };
  }

  const packageItems = packageData.items || [];
  const categoriesExceeded = [];

  // Check each category
  categoriesData.forEach(category => {
    // Support both choose_rule and selection_rules naming conventions
    const rules = category.choose_rule || category.selection_rules;
    if (!rules || !Array.isArray(rules)) {
      return;
    }

    // Get the limit for the NEW (lower) PAX
    let newPaxLimit = 0;
    if (category.selection_rules) {
      // New structure with pax_range and max_selections
      const sortedRules = [...rules].sort((a, b) => 
        parseInt(b.pax_range?.min || 0) - parseInt(a.pax_range?.min || 0)
      );
      const matchingRule = sortedRules.find(rule => 
        newPax >= parseInt(rule.pax_range?.min || 0) && 
        newPax <= parseInt(rule.pax_range?.max || 999)
      );
      newPaxLimit = matchingRule ? parseInt(matchingRule.max_selections || 0) : 0;
    } else {
      // Old structure with pax and choose
      const sortedRules = [...rules].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      const matchingRule = sortedRules.find(rule => 
        newPax >= parseInt(rule.pax)
      );
      newPaxLimit = matchingRule ? parseInt(matchingRule.choose || 0) : 0;
    }

    // Count items in this category (excluding add-ons and complimentary items)
    const itemsInCategory = packageItems.filter(item => {
      // Only count items in this category
      if (item.category_id !== category.id) return false;
      
      // Exclude add-on items (half/full)
      if (item.add_on_type) return false;
      
      // Exclude complimentary items (items where all quantity is complimentary)
      if (item.complimentaryCount && item.complimentaryCount >= item.quantity) return false;
      
      return true;
    });

    // Count total non-complimentary quantity
    const totalQuantity = itemsInCategory.reduce((total, item) => {
      const nonComplimentaryQty = item.quantity - (item.complimentaryCount || 0);
      
      // For variant items, sum up variant quantities
      if (item.has_variants && item.items) {
        return total + item.items.reduce((variantTotal, variantItem) => 
          variantTotal + (variantItem.quantity || 0), 0
        );
      }
      
      return total + Math.max(0, nonComplimentaryQty);
    }, 0);

    // If current items exceed the new limit, track this category
    if (newPaxLimit > 0 && totalQuantity > newPaxLimit) {
      categoriesExceeded.push({
        categoryId: category.id,
        categoryName: category.name_en || category.name_ar || 'Items',
        currentItems: totalQuantity,
        newLimit: newPaxLimit,
        excessItems: totalQuantity - newPaxLimit
      });
    }
  });

  return {
    canDecrease: categoriesExceeded.length === 0,
    categoriesExceeded
  };
};

/**
 * Validate if all required package items have been added based on categories structure
 * @param {Object} packageData - Package object containing categories and items
 * @param {number} paxNumber - Selected PAX number from filters
 * @returns {Object} - Validation result with isValid flag and missing categories info
 */
export const validatePackageCompletion = (packageData, paxNumber) => {
  if (!packageData || !paxNumber) {
    return { 
      isValid: false, 
      missingCategories: [],
      message: 'Invalid package data or PAX number' 
    };
  }

  const { categories: categoriesData, items: packageItems } = packageData;

  if (!categoriesData || !Array.isArray(categoriesData)) {
    return { 
      isValid: true, 
      missingCategories: [],
      message: 'No categories defined - package is valid' 
    };
  }

  // NEW: Skip validation for standalone packages (like "Additional Setup")
  // ONLY for specific packages - NOT for "Additional Services"
  // These are pure add-on items where each item charges its own price
  
  // Check package name first
  const packageName = packageData.name_en || packageData.title || '';
  const isAdditionalSetup = packageName.toLowerCase().includes('additional setup');
  
  if (isAdditionalSetup) {
    // Check BOTH cart items AND category items for standalone detection
    const hasStandaloneInCart = packageItems?.some(item => item.price_type === "Standalone Item Based");
    const hasStandaloneInCategories = categoriesData?.some(category => 
      category.catering_service_item?.some(item => item.price_type === "Standalone Item Based")
    );
    
    if (hasStandaloneInCart || hasStandaloneInCategories) {
      return { 
        isValid: true, 
        missingCategories: [],
        message: 'Standalone package (Additional Setup) - no validation required' 
      };
    }
  }

  const missingCategories = [];
  let totalRequiredItems = 0;
  let totalAddedItems = 0;

  // Check each category
  categoriesData.forEach(category => {
    // Support both choose_rule and selection_rules naming conventions
    const rules = category.choose_rule || category.selection_rules;
    if (!rules || !Array.isArray(rules)) {
      return;
    }

    let matchingRule = null;
    let requiredItemsForCategory = 0;

    // Handle different rule structures
    if (category.selection_rules) {
      // New structure with pax_range and max_selections
      const sortedRules = [...rules].sort((a, b) => 
        parseInt(b.pax_range?.min || 0) - parseInt(a.pax_range?.min || 0)
      );
      matchingRule = sortedRules.find(rule => 
        paxNumber >= parseInt(rule.pax_range?.min || 0) && 
        paxNumber <= parseInt(rule.pax_range?.max || 999)
      );
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.max_selections || 0) : 0;
    } else {
      // Old structure with pax and choose
      const sortedRules = [...rules].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      matchingRule = sortedRules.find(rule => 
        paxNumber >= parseInt(rule.pax)
      );
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.choose || 0) : 0;
    }

    if (matchingRule && requiredItemsForCategory > 0) {
      totalRequiredItems += requiredItemsForCategory;
      
      // Debug logging for PAX-based requirements
      const ruleDescription = category.selection_rules 
        ? `PAX ${matchingRule.pax_range?.min}-${matchingRule.pax_range?.max} -> ${matchingRule.max_selections}`
        : `PAX ${matchingRule.pax} -> ${matchingRule.choose}`;
      console.log(`PAX ${paxNumber} - Category "${category.name_en}": requires ${requiredItemsForCategory} items (rule: ${ruleDescription})`);

      // Count items added for this category
      // IMPORTANT: Only count "normal" items (no add_on_type) toward the category requirement
      // Add-on items (half/full) are additional chargeable items and don't count toward the free limit
      const itemsInCategory = packageItems ? packageItems.filter(item => 
        item.category_id === category.id && !item.add_on_type
      ) : [];
      
      // Debug logging - remove this once validation is working
      if (process.env.NODE_ENV === 'development') {
        // Also log add-on items for debugging
        const addOnItemsInCategory = packageItems ? packageItems.filter(item => 
          item.category_id === category.id && item.add_on_type
        ) : [];
        
        console.log('Validation Debug - Category Check:', {
          categoryId: category.id,
          categoryName: category.name_en,
          paxNumber: paxNumber,
          requiredItems: requiredItemsForCategory,
          normalItemsCount: itemsInCategory.length,
          addOnItemsCount: addOnItemsInCategory.length,
          normalItems: itemsInCategory.map(item => ({
            id: item.id,
            name: item.name || item.name_en,
            category_id: item.category_id,
            quantity: item.quantity,
            has_variants: item.has_variants,
            add_on_type: item.add_on_type || 'normal'
          })),
          addOnItems: addOnItemsInCategory.map(item => ({
            id: item.id,
            name: item.name || item.name_en,
            add_on_type: item.add_on_type,
            quantity: item.quantity
          }))
        });
      }

      // Calculate total quantity for this category (including variant items)
      const totalCategoryQuantity = itemsInCategory.reduce((total, item) => {
        if (item.has_variants && item.items) {
          // For variant items, sum up all variant quantities
          return total + item.items.reduce((variantTotal, variantItem) => 
            variantTotal + (variantItem.quantity || 0), 0
          );
        } else {
          // For regular items, just add the quantity
          return total + (item.quantity || 0);
        }
      }, 0);

      totalAddedItems += totalCategoryQuantity;

      // Check if this category has enough items
      if (totalCategoryQuantity < requiredItemsForCategory) {
        missingCategories.push({
          categoryId: category.id,
          categoryName: category.name_en || category.name_ar || 'Items',
          required: requiredItemsForCategory,
          added: totalCategoryQuantity,
          missing: requiredItemsForCategory - totalCategoryQuantity
        });
      }
    }
  });

  const isValid = missingCategories.length === 0;
  
  return {
    isValid,
    missingCategories,
    totalRequiredItems,
    totalAddedItems,
    currentPax: paxNumber,
    message: isValid 
      ? `All required items have been added for ${paxNumber} PAX`
      : `Missing items in ${missingCategories.length} categories for ${paxNumber} PAX`
  };
};